import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { WC2026_COUNTRIES } from '@/lib/wc2026-countries';

import { CreatorMvpNote } from './CreatorMvpNote';
import {
  useCreatorDetail,
  useCreatorStats,
  useReviewCreator,
  useReviewCreatorPrize,
  useReviewCreatorSocial,
  useUpdateCreatorCompetition,
  useUpdateCreatorProfile,
} from './useCreators';
import type {
  CreatorCompetition,
  CreatorDetail,
  CreatorPrize,
  CreatorSetupStage,
  CreatorSocial,
  CreatorStatsResponse,
} from './types';

interface CreatorDrawerProps {
  userId: string | null;
  onClose: () => void;
}

/**
 * Derive the public agent-app base URL from VITE_ENV. Avoids adding a new
 * Vercel env var: the admin already sets VITE_ENV per branch (local/dev/
 * production), and the agent-app domain is canonical per-environment.
 */
function agentAppBaseUrl(): string {
  const env = (import.meta.env.VITE_ENV as string | undefined)?.toLowerCase();
  if (env === 'production') return 'https://app.fantopy.ai';
  if (env === 'dev') return 'https://dev.fantopy.ai';
  return 'http://localhost:4201';
}

export function CreatorDrawer({ userId, onClose }: CreatorDrawerProps) {
  const open = Boolean(userId);
  const detailQuery = useCreatorDetail(userId);
  const statsQuery = useCreatorStats(userId);
  const reviewCreator = useReviewCreator();
  const reviewSocial = useReviewCreatorSocial();
  const reviewPrize = useReviewCreatorPrize();
  const updateProfile = useUpdateCreatorProfile();
  const updateCompetition = useUpdateCreatorCompetition();

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <SheetContent className="w-[720px] max-w-full overflow-y-auto sm:max-w-[720px]">
        {detailQuery.isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : detailQuery.isError ? (
          <div className="py-12 text-center text-sm text-destructive">Failed to load creator.</div>
        ) : detailQuery.data ? (
          <>
            <SheetHeader>
              <SheetTitle>
                {detailQuery.data.display_name ?? detailQuery.data.user.email ?? 'Creator'}
              </SheetTitle>
              <SheetDescription>
                {detailQuery.data.public_slug
                  ? `/join/${detailQuery.data.public_slug}`
                  : 'No public slug'}
              </SheetDescription>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge variant={reviewBadgeVariant(detailQuery.data.status)}>
                  Review: {reviewVerdictLabel(detailQuery.data.status)}
                </Badge>
                <Badge variant={setupBadgeVariant(detailQuery.data.setup_status)}>
                  Setup: {setupStatusLabel(detailQuery.data.setup_status)}
                </Badge>
              </div>
            </SheetHeader>

            <div className="mt-6 space-y-5 text-sm">
              <CreatorMvpNote />

              <Section title="Review">
                <ReviewControls
                  creator={detailQuery.data}
                  reviewing={reviewCreator.isPending}
                  onReview={(args) => reviewCreator.mutate(args)}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {detailQuery.data.public_slug ? (
                    <a
                      className="inline-flex items-center rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
                      href={`${agentAppBaseUrl()}/join/${detailQuery.data.public_slug}?preview=1`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Preview public profile ↗
                    </a>
                  ) : null}
                  {detailQuery.data.website_url ? (
                    <a
                      className="inline-flex items-center rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
                      href={detailQuery.data.website_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Visit creator website ↗
                    </a>
                  ) : null}
                </div>
                {detailQuery.data.bio ? (
                  <p className="mt-3 whitespace-pre-wrap">{detailQuery.data.bio}</p>
                ) : null}
              </Section>

              <Section title="Performance">
                <CreatorStatsCard
                  loading={statsQuery.isLoading}
                  error={statsQuery.isError}
                  data={statsQuery.data ?? null}
                />
              </Section>

              <Section title="Public profile visuals">
                <ProfileVisualEditor
                  creator={detailQuery.data}
                  saving={updateProfile.isPending}
                  onSave={(body) =>
                    updateProfile.mutate({ userId: detailQuery.data!.user_id, body })
                  }
                />
              </Section>

              <Section title="Social proof">
                <div className="space-y-2">
                  {detailQuery.data.socials.map((s) => (
                    <SocialRow
                      key={s.id}
                      social={s}
                      onReview={(status) => reviewSocial.mutate({ id: s.id, status })}
                    />
                  ))}
                  {detailQuery.data.socials.length === 0 ? (
                    <p className="text-muted-foreground">No socials submitted.</p>
                  ) : null}
                </div>
              </Section>

              <Section title="Prize/content submissions">
                <div className="space-y-2">
                  {detailQuery.data.prizes.map((p) => {
                    const linkedComp = detailQuery.data!.competitions.find(
                      (c) => c.id === p.competition_id,
                    );
                    return (
                      <PrizeRow
                        key={p.id}
                        prize={p}
                        linkedCompetitionTitle={linkedComp?.title ?? null}
                        onReview={(status) => reviewPrize.mutate({ id: p.id, status })}
                      />
                    );
                  })}
                  {detailQuery.data.prizes.length === 0 ? (
                    <p className="text-muted-foreground">No prizes submitted.</p>
                  ) : null}
                </div>
              </Section>

              <Section title="Creator competitions">
                <div className="space-y-2">
                  {detailQuery.data.competitions.map((c) => (
                    <CompetitionRow
                      key={c.id}
                      competition={c}
                      saving={updateCompetition.isPending}
                      onSave={(body) => updateCompetition.mutate({ id: c.id, body })}
                    />
                  ))}
                  {detailQuery.data.competitions.length === 0 ? (
                    <p className="text-muted-foreground">No creator competitions.</p>
                  ) : null}
                </div>
              </Section>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function ProfileVisualEditor({
  creator,
  saving,
  onSave,
}: {
  creator: CreatorDetail;
  saving: boolean;
  onSave: (body: {
    display_name?: string;
    public_slug?: string;
    bio?: string | null;
    avatar_url?: string | null;
    banner_url?: string | null;
    website_url?: string | null;
    accent_color?: string | null;
    share_headline?: string | null;
    share_body?: string | null;
    world_cup_country?: string | null;
  }) => void;
}) {
  const [draft, setDraft] = useState({
    display_name: creator.display_name ?? '',
    public_slug: creator.public_slug ?? '',
    bio: creator.bio ?? '',
    avatar_url: creator.avatar_url ?? '',
    banner_url: creator.banner_url ?? '',
    website_url: creator.website_url ?? '',
    accent_color: creator.accent_color ?? '#00d4aa',
    share_headline: creator.share_headline ?? '',
    share_body: creator.share_body ?? '',
    world_cup_country: creator.world_cup_country ?? '',
  });

  useEffect(() => {
    setDraft({
      display_name: creator.display_name ?? '',
      public_slug: creator.public_slug ?? '',
      bio: creator.bio ?? '',
      avatar_url: creator.avatar_url ?? '',
      banner_url: creator.banner_url ?? '',
      website_url: creator.website_url ?? '',
      accent_color: creator.accent_color ?? '#00d4aa',
      share_headline: creator.share_headline ?? '',
      share_body: creator.share_body ?? '',
      world_cup_country: creator.world_cup_country ?? '',
    });
  }, [creator.user_id]);

  return (
    <div className="space-y-2 rounded-md border bg-background p-3">
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={draft.display_name}
          placeholder="Display name"
          onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
        />
        <Input
          value={draft.public_slug}
          placeholder="public-slug"
          onChange={(e) => setDraft({ ...draft, public_slug: e.target.value })}
        />
        <Input
          value={draft.avatar_url}
          placeholder="Avatar image URL"
          onChange={(e) => setDraft({ ...draft, avatar_url: e.target.value })}
        />
        <Input
          value={draft.banner_url}
          placeholder="Banner image URL"
          onChange={(e) => setDraft({ ...draft, banner_url: e.target.value })}
        />
        <Input
          value={draft.website_url}
          placeholder="Website URL"
          onChange={(e) => setDraft({ ...draft, website_url: e.target.value })}
        />
        <Input
          value={draft.accent_color}
          placeholder="#00d4aa"
          onChange={(e) => setDraft({ ...draft, accent_color: e.target.value })}
        />
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={draft.world_cup_country}
          onChange={(e) => setDraft({ ...draft, world_cup_country: e.target.value })}
        >
          <option value="">— Country they support —</option>
          {WC2026_COUNTRIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      <Input
        value={draft.share_headline}
        placeholder="Share headline"
        onChange={(e) => setDraft({ ...draft, share_headline: e.target.value })}
      />
      <textarea
        className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        value={draft.bio}
        placeholder="Bio"
        onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
      />
      <textarea
        className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        value={draft.share_body}
        placeholder="Share copy"
        onChange={(e) => setDraft({ ...draft, share_body: e.target.value })}
      />
      <Button
        size="sm"
        disabled={saving}
        onClick={() =>
          onSave({
            ...draft,
            avatar_url: draft.avatar_url || null,
            banner_url: draft.banner_url || null,
            website_url: draft.website_url || null,
            bio: draft.bio || null,
            share_headline: draft.share_headline || null,
            share_body: draft.share_body || null,
            accent_color: draft.accent_color || null,
            world_cup_country: draft.world_cup_country || null,
          })
        }
      >
        Save public profile
      </Button>
    </div>
  );
}

/**
 * Approve / Reject controls split out so the gating + reject-reason
 * capture stay self-contained.
 *
 * Behaviour:
 *   - Approve is disabled until the creator has actually submitted
 *     (`status === 'pending'` AND `setup_status === 'in_review'`).
 *     A muted explainer next to the button tells the admin why.
 *     Previously Approve was visible whenever `status === 'pending'`,
 *     which is the default for any creator profile — admins could
 *     accidentally approve a creator with no profile / no manager / no
 *     competition and their public page would go live with empty fields.
 *   - Reject opens an inline textarea so the admin can leave a note that
 *     the creator sees on their Studio rejected banner. Empty notes are
 *     allowed but actively discouraged with the helper copy.
 */
function ReviewControls({
  creator,
  reviewing,
  onReview,
}: {
  creator: CreatorDetail;
  reviewing: boolean;
  onReview: (args: {
    userId: string;
    status: 'approved' | 'rejected' | 'pending';
    review_note?: string;
  }) => void;
}) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const canReview =
    creator.status === 'pending' && creator.setup_status === 'in_review';
  const stalledReason =
    creator.status === 'pending'
      ? creator.setup_status === 'incomplete'
        ? 'Creator hasn’t finished setup yet.'
        : creator.setup_status === 'ready_to_submit'
          ? 'Creator is ready but hasn’t tapped Submit yet.'
          : null
      : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {creator.status === 'pending' ? (
          <>
            <Button
              size="sm"
              disabled={!canReview || reviewing}
              onClick={() => onReview({ userId: creator.user_id, status: 'approved' })}
            >
              Approve creator
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!canReview || reviewing}
              onClick={() => setShowRejectForm((v) => !v)}
            >
              Reject…
            </Button>
            {stalledReason ? (
              <span className="text-xs text-muted-foreground">{stalledReason}</span>
            ) : null}
          </>
        ) : null}
        {creator.status === 'approved' ? <Badge>Creator approved</Badge> : null}
        {creator.status === 'rejected' ? <Badge variant="destructive">Rejected</Badge> : null}
        {creator.status === 'approved' || creator.status === 'rejected' ? (
          <Button
            size="sm"
            variant="outline"
            disabled={reviewing}
            onClick={() => onReview({ userId: creator.user_id, status: 'pending' })}
          >
            Move to pending
          </Button>
        ) : null}
      </div>

      {/* Surface the prior review note so admins iterating on a rejection
          can see what the creator was told (and creators see it on their
          rejected banner via the same `review_note` field). */}
      {creator.review_note ? (
        <div className="rounded-md border bg-muted/40 p-3 text-xs">
          <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
            Prior review note
          </div>
          <p className="whitespace-pre-wrap">{creator.review_note}</p>
        </div>
      ) : null}

      {showRejectForm && canReview ? (
        <div className="rounded-md border bg-background p-3">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Note for the creator (visible on their rejected banner)
          </label>
          <textarea
            className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            rows={3}
            value={rejectReason}
            placeholder="What does the creator need to fix?"
            onChange={(e) => setRejectReason(e.target.value.slice(0, 2000))}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="destructive"
              disabled={reviewing}
              onClick={() => {
                onReview({
                  userId: creator.user_id,
                  status: 'rejected',
                  review_note: rejectReason.trim() || undefined,
                });
                setShowRejectForm(false);
                setRejectReason('');
              }}
            >
              {reviewing ? 'Rejecting…' : 'Confirm reject'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={reviewing}
              onClick={() => {
                setShowRejectForm(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function SocialRow({
  social,
  onReview,
}: {
  social: CreatorSocial;
  onReview: (status: 'APPROVED' | 'REJECTED' | 'PENDING') => void;
}) {
  const isApproved = social.status === 'APPROVED';
  const isRejected = social.status === 'REJECTED';
  return (
    <div className="rounded-md border bg-background p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="font-medium">
            {social.platform}: @{social.handle}
          </div>
          {social.url ? (
            <a className="text-xs text-primary" href={social.url}>
              {social.url}
            </a>
          ) : null}
        </div>
        <Badge>{social.status}</Badge>
      </div>
      {!isApproved && !isRejected ? (
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onReview('APPROVED')}>
            Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReview('REJECTED')}>
            Reject
          </Button>
        </div>
      ) : (
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onReview('PENDING')}>
            Move to pending
          </Button>
        </div>
      )}
    </div>
  );
}

function PrizeRow({
  prize,
  linkedCompetitionTitle,
  onReview,
}: {
  prize: CreatorPrize;
  linkedCompetitionTitle: string | null;
  onReview: (status: 'APPROVED' | 'REJECTED' | 'PENDING') => void;
}) {
  const isApproved = prize.status === 'APPROVED';
  const isRejected = prize.status === 'REJECTED';
  return (
    <div className="rounded-md border bg-background p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium">{prize.title}</div>
          <div className="text-xs text-muted-foreground">
            For:{' '}
            {linkedCompetitionTitle ?? (
              <span className="italic">no competition linked</span>
            )}
          </div>
        </div>
        <Badge>{prize.status}</Badge>
      </div>
      {prize.description ? <p className="mt-2 text-sm">{prize.description}</p> : null}
      {!isApproved && !isRejected ? (
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onReview('APPROVED')}>
            Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReview('REJECTED')}>
            Reject
          </Button>
        </div>
      ) : (
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onReview('PENDING')}>
            Move to pending
          </Button>
        </div>
      )}
    </div>
  );
}

function CompetitionRow({
  competition,
  onSave,
  saving,
}: {
  competition: CreatorCompetition;
  onSave: (body: {
    creator_cover_url?: string | null;
    creator_join_opens_at?: string | null;
    entry_deadline?: string | null;
    creator_share_headline?: string | null;
    creator_share_body?: string | null;
  }) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState({
    creator_cover_url: competition.creator_cover_url ?? '',
    creator_join_opens_at: competition.creator_join_opens_at
      ? competition.creator_join_opens_at.slice(0, 16)
      : '',
    entry_deadline: competition.entry_deadline ? competition.entry_deadline.slice(0, 16) : '',
    creator_share_headline: competition.creator_share_headline ?? '',
    creator_share_body: competition.creator_share_body ?? '',
  });

  useEffect(() => {
    setDraft({
      creator_cover_url: competition.creator_cover_url ?? '',
      creator_join_opens_at: competition.creator_join_opens_at
        ? competition.creator_join_opens_at.slice(0, 16)
        : '',
      entry_deadline: competition.entry_deadline ? competition.entry_deadline.slice(0, 16) : '',
      creator_share_headline: competition.creator_share_headline ?? '',
      creator_share_body: competition.creator_share_body ?? '',
    });
  }, [competition.id]);

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="font-medium">{competition.title}</div>
          <div className="text-xs text-muted-foreground">
            {competition.visibility} · {competition.entries_count} entries ·{' '}
            {competition.slug ?? 'no slug'}
          </div>
        </div>
      </div>
      <div className="mt-3 grid gap-2">
        <Input
          value={draft.creator_cover_url}
          placeholder="Competition cover image URL"
          onChange={(e) => setDraft({ ...draft, creator_cover_url: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="datetime-local"
            value={draft.creator_join_opens_at}
            onChange={(e) => setDraft({ ...draft, creator_join_opens_at: e.target.value })}
          />
          <Input
            type="datetime-local"
            value={draft.entry_deadline}
            onChange={(e) => setDraft({ ...draft, entry_deadline: e.target.value })}
          />
        </div>
        <Input
          value={draft.creator_share_headline}
          placeholder="Competition share headline"
          onChange={(e) => setDraft({ ...draft, creator_share_headline: e.target.value })}
        />
        <textarea
          className="min-h-16 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={draft.creator_share_body}
          placeholder="Competition share copy"
          onChange={(e) => setDraft({ ...draft, creator_share_body: e.target.value })}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={saving}
          onClick={() =>
            onSave({
              creator_cover_url: draft.creator_cover_url || null,
              creator_join_opens_at: draft.creator_join_opens_at
                ? new Date(draft.creator_join_opens_at).toISOString()
                : null,
              entry_deadline: draft.entry_deadline
                ? new Date(draft.entry_deadline).toISOString()
                : null,
              creator_share_headline: draft.creator_share_headline || null,
              creator_share_body: draft.creator_share_body || null,
            })
          }
        >
          Save competition visuals
        </Button>
      </div>
    </div>
  );
}

function reviewVerdictLabel(status: CreatorDetail['status'] | null): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'pending':
      return 'Pending';
    default:
      return '—';
  }
}

function reviewBadgeVariant(
  status: CreatorDetail['status'] | null,
): 'default' | 'destructive' | 'outline' {
  if (status === 'approved') return 'default';
  if (status === 'rejected') return 'destructive';
  return 'outline';
}

function setupStatusLabel(stage: CreatorSetupStage | null): string {
  switch (stage) {
    case 'incomplete':
      return 'Incomplete';
    case 'ready_to_submit':
      return 'Ready to submit';
    case 'in_review':
      return 'In review';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Unknown';
  }
}

function setupBadgeVariant(
  stage: CreatorSetupStage | null,
): 'default' | 'destructive' | 'outline' {
  if (stage === 'approved') return 'default';
  if (stage === 'rejected') return 'destructive';
  return 'outline';
}

/**
 * All-time + last-7-days counts pulled from `CreatorAttributionEvent`.
 * Numbers in monospace, two-column layout, no charts.
 */
function CreatorStatsCard({
  loading,
  error,
  data,
}: {
  loading: boolean;
  error: boolean;
  data: CreatorStatsResponse | null;
}) {
  if (loading) {
    return (
      <div className="rounded-md border bg-background p-3 text-sm text-muted-foreground">
        Loading stats…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-md border bg-background p-3 text-sm text-muted-foreground">
        Stats unavailable.
      </div>
    );
  }

  const rows: Array<{ label: string; key: 'views' | 'leads' | 'accounts' | 'entries' }> = [
    { label: 'Views', key: 'views' },
    { label: 'Leads', key: 'leads' },
    { label: 'Accounts', key: 'accounts' },
    { label: 'Entries', key: 'entries' },
  ];

  return (
    <div className="rounded-md border bg-background">
      <div className="grid grid-cols-2 border-b text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <div className="px-3 py-2">All time</div>
        <div className="border-l px-3 py-2">Last 7 days</div>
      </div>
      <div className="grid grid-cols-2">
        <ul className="divide-y">
          {rows.map((row) => (
            <li
              key={`all-${row.key}`}
              className="flex items-center justify-between px-3 py-2 text-sm"
            >
              <span className="text-muted-foreground">{row.label}</span>
              <span className="font-mono tabular-nums">
                {data.all_time[row.key].toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
        <ul className="divide-y border-l">
          {rows.map((row) => (
            <li
              key={`recent-${row.key}`}
              className="flex items-center justify-between px-3 py-2 text-sm"
            >
              <span className="text-muted-foreground">{row.label}</span>
              <span className="font-mono tabular-nums">
                {data.last_7d[row.key].toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
